import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ErrorBoundary from '../components/ErrorBoundary'

import {
	FormControl,
	RadioGroup,
	FormControlLabel,
	MenuItem,
	Radio,
	TextField,
	Tooltip,
	Switch,
	Typography,
	Box,
} from "@mui/material"

import dayjs from 'dayjs'

import {
	AttachMoney as AttachMoneyIcon,
	ArrowForwardIos as ArrowForwardIosIcon,
	CheckCircleOutline as CheckCircleOutlineIcon,
	CreditCardOutlined as CreditCardOutlinedIcon,
	ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
	InfoOutlined as InfoOutlinedIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon,
	LockOutlined as LockOutlinedIcon,
	NorthEast as NorthEastIcon,
	ScheduleOutlined as ScheduleOutlinedIcon,
	MarkEmailReadOutlined as MarkEmailReadOutlinedIcon,
	Search as SearchIcon,
	Groups as GroupsIcon,
	ForumOutlined as ForumOutlinedIcon,
	Message as MessageIcon,
	Smartphone as SmartphoneIcon,
	Discount as DiscountIcon,
	MoneyOff as MoneyOffIcon,
	TrendingUp as TrendingUpIcon,
	Business as BusinessIcon,
	Celebration as CelebrationIcon,
	SentimentDissatisfied as SentimentDissatisfiedIcon,
} from '@mui/icons-material'

import DefaultModal from '../components/modal/DefaultModal/DefaultModal'
import EmptyScoutNumbersGate from '../components/home/EmptyScoutNumbersGate'
import FirstConnectionGate from '../components/home/FirstConnectionGate'
import FloatingSidebarCard from '../components/home/FloatingSidebarCard'
import HomeGraphicsSection from '../components/home/graphics'
import { useHomeAccountsPolling } from '../components/home/useHomeAccountsPolling'
import ScoutAvatar from '../components/ScoutAvatar'
import MetricTrend from "../components/ui/MetricTrend"
import SearchResultsModal from '../components/search/SearchResultsModal'
import {
	normalizeQueryText,
} from '../components/search/SearchHighlight'
import { useSnackbar } from '../components/SnackbarContext'
import { RoundedButton } from "../components/ui/roundButton"

import { useNavigate, useSearchParams } from 'react-router-dom'

import {
	formatDateLabel,
	formatPhoneNumber,
	getDefaultScoutDatePreset,
	getDefaultScoutDatePresetValue,
	getInitials,
	listScoutDateOptions,
	normalizeScoutDate,
	getPreferredAccount,
	getScoutAccountAnalytics,
	getScoutAccountDetails,
	getScoutEnterprise,
	getScoutContext,
	isScoutAccountActive,
	listScoutAccountsPage,
	DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
	SCOUT_ACCOUNT_FILTER_OPTIONS,
	searchScoutMessages,
	renotifyScoutAccount,
	checkoutStripePlan,
	changeStripePlan,
	listStripePlans,
	cancelStripeSubscription,
	reactivateStripeSubscription,
	updateEnterpriseDocument,
	payOverdue,
	activateSubscription,
} from '../services/scout'

import {
	getHomeAccountDecorator,
	getHomeDashboardConfig,
	resolveHomeValue,
} from '../services/home-config'

import {
	getMaxLicenses,
	isTrial,
	isFreePlan,
	getPlanEndDate,
	isAccountBlocked,
	userHasGroup
} from "../services/utils"
import { openSupportForm } from '../services/support'

import { IMaskInput } from "react-imask"

import {
	formatBRL,
	isValidCPF,
	isValidCNPJ,
	getBillingCycleProgress,
	getGreetings
} from "../utils"

import { loader } from '../lib/loader'

const GREETING_CONFIG = {
    activation_success: {
        icon: <CelebrationIcon />,
        message: "Sua assinatura foi ativada com sucesso!",
        sub: "Aproveite todos os benefícios."
    },
    upgrade_success: {
        icon: <CelebrationIcon />,
        message: "Seu plano foi atualizado com sucesso!",
        sub: "Aproveite os novos benefícios."
    },
    subscription_cancelled: {
        icon: <SentimentDissatisfiedIcon />,
        message: "Assinatura cancelada com sucesso!",
        sub: "O acesso permanece até o fim do período vigente."
    },
    subscription_payed_overdue: {
        icon: <CelebrationIcon />,
        message: "Assinatura regularizada com sucesso!",
        sub: "Volte a aproveitar todos os benefícios."
    },
    subscription_reactivated: {
        icon: <CelebrationIcon />,
        message: "Assinatura reativada com sucesso!",
        sub: "Aproveite novamente todos os benefícios."
    }
}

const SCOUT_PAYMENT_PLANS = [
	{
		slug: "free",
		name: "Free",
        description: "Experimente sem compromisso",
		icon: <MoneyOffIcon />,
		rank: 1,
		price: 0,
		priceSuffix: "/mês",
		buttonLabel: "Assinar plano",
		features: [
            "1 número monitorado",
            "Histórico de 7 dias",
            "Registra texto e áudio",
            "Dashboard analítico",
            "Busca por termos",
            "1 administrador",
            "Conformidade LGPD",
            "Infraestrutura no Brasil",
            // "Histórico de 7 dias",
        ],
	},
	{
		slug: "starter",
		name: "Starter",
        description: "Para pequenos times",
		icon: <GroupsIcon />,
		rank: 2,
		price: 197,
		priceSuffix: "/mês",
		buttonLabel: "Assinar plano",
		features: [
            "Todos os recursos do Free",
            "5 números monitorados",
            "Histórico de 60 dias",
            "Registra todas as mídias",
            "Lista de exclusão",
            "Alertas por palavras-chave",
            "Exportação de conversas",
            "Suporte 8x5",
        ],
	},
	{
		slug: "growth",
		name: "Growth",
        description: "Equipes em operação real",
		icon: <TrendingUpIcon />,
		rank: 3,
		price: 447,
		priceSuffix: "/mês",
		buttonLabel: "Assinar plano",
		popular: true,
		badge: "Mais Popular",
		features: [
            "Todos os recursos do Starter",
            "20 números monitorados",
            "Histórico de 365 dias, sem limite de espaço de armazenamento",
            "IA e insights automáticos",
            "até 2 gestores",
            "Notificações por e-mail",
            "Gestão por equipes",
            "Exportação automática",
        ],
	},
	{
		slug: "enterprise",
		name: "Enterprise",
        description: "Regulação, integração e escala",
		icon: <BusinessIcon />,
		rank: 4,
		price: null,
		priceLabel: "Sob consulta",
		buttonLabel: "Saiba Mais",
		contactOnly: true,
		features: [
            "Todos os recursos do Growth",
            "Números sob medida",
            "Histórico ilimitado *",
            "Gestores ilimitados",
            "CRM nativo",
            "Onboarding assistido",
            "Suporte dedicado",
            "Gerente de conta dedicado",
            "SSO (Single Sign-On)",
        ],
	},
]

const TRACKED_SCOUT_PLAN_SLUGS = new Set(['free', 'starter', 'growth'])

const CPF_MASK = "000.000.000-00"
const CNPJ_MASK = "AA.AAA.AAA/AAAA-00"

const AccountAvatar = ({ account, decorator, active }) => {
	const classNames = ['scout-account-card__avatar']

	if (active) {
		classNames.push('scout-account-card__avatar--online')
	}

	if (decorator?.avatarTone) {
		classNames.push(`scout-account-card__avatar--${decorator.avatarTone}`)
	}

	return (
		<ScoutAvatar
			entity={account}
			alt={account?.name || 'Conta'}
			className={classNames.join(' ')}
			fallback={() => <div className={classNames.join(' ')}>{getInitials(account?.name)}</div>}
		/>
	)
}

const SidebarCardIcon = () => {
	return <CreditCardOutlinedIcon />
}

const getAccountStatusIcon = (statusIcon) => {
	if (statusIcon === 'lock') {
		return <LockOutlinedIcon className="scout-account-card__status-icon" />
	}

	if (statusIcon === 'clock') {
		return <ScheduleOutlinedIcon className="scout-account-card__status-icon" />
	}

	return <ArrowForwardIosIcon className="scout-account-card__arrow" />
}

const getAccountStatusTooltip = ({ active, statusIcon }) => {
	if (!active && statusIcon === 'clock') {
		return 'Número desconectado. Você ainda pode visualizar o histórico de conversas.'
	}

	return ''
}

const PlanPrice = ({ plan, annualDiscount }) => {
	if (typeof plan?.price === 'number') {
		const price = (annualDiscount)
			? formatBRL(plan.monthlyValue - plan.discountValue)
			: formatBRL(plan.monthlyValue || plan.price)
		return (
			<div className="scout-plan-card__price">
				<strong>{price}</strong>
				<span>{plan.priceSuffix || ''}</span>
			</div>
		)
	}

	return <div className="scout-plan-card__price">
		<strong>{plan?.priceLabel || 'Sob consulta'}</strong>
		<span></span>
	</div>
}

const slugifyPlanName = (value = '') => {
	return String(value)
		.trim()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '')
}

const resolveScoutPlanSlug = (value = '') => {
	return slugifyPlanName(value).replace(/^scout-/, '')
}

const resolveCheckoutPlan = (plan, stripePlanBySlug) => {
	const planSlug = resolveScoutPlanSlug(plan?.slug || plan?.name || '')
	return stripePlanBySlug[planSlug] || plan || null
}

const canRenderUpgradePlan = (plan, currentPlanRank) => {
	return Boolean(plan?.contactOnly || plan?.rank > currentPlanRank)
}

const getTrialStatus = (enterprise) => {
	if (!isTrial(enterprise)) {
		return null
	}

	const trialEndAt = dayjs(getPlanEndDate(enterprise))
	const today = dayjs().startOf('day')

	if (!trialEndAt.isValid()) {
		return null
	}

	const diffInDays = trialEndAt.startOf('day').diff(today, 'day')

	if (diffInDays < 0) {
		return {
			kind: 'expired',
			daysRemaining: 0,
			progress: 100,
		}
	}

	const daysRemaining = diffInDays
	const progress = Math.max(0, Math.min(100, ((7 - Math.min(daysRemaining, 7)) / 7) * 100))

	if (daysRemaining === 0) {
		return {
			kind: 'last-day',
			daysRemaining,
			progress: 100,
		}
	}

	if (daysRemaining === 1) {
		return {
			kind: 'last-day',
			daysRemaining,
			progress: Math.max(progress, 86),
		}
	}

	if (daysRemaining <= 2) {
		return {
			kind: 'critical',
			daysRemaining,
			progress: Math.max(progress, 86),
		}
	}

	if (daysRemaining <= 7) {
		return {
			kind: 'warning',
			daysRemaining,
			progress,
		}
	}

	return {
		kind: 'active',
		daysRemaining,
		progress,
	}
}

const getTrialCopy = (trialStatus) => {
	if (!trialStatus) {
		return null
	}

	if (trialStatus.kind === 'expired') {
		return {
			cardTitle: 'Período de teste encerrado',
			cardDescription: 'Assine para recuperar seu acesso',
			modalTitle: null,
			modalDescription: 'Assine agora mesmo e não perca seus dados',
		}
	}

	if (trialStatus.kind === 'last-day') {
		if (trialStatus.daysRemaining === 0) {
			return {
				cardTitle: 'Não perca seus dados!',
				cardDescription: (
					<>
						Seu período de teste encerra <strong>hoje</strong>
					</>
				),
				modalTitle: 'Seu período de teste gratuito do Kwik Scout encerra hoje',
				modalDescription: 'Assine agora mesmo e não perca seus dados',
			}
		}

		return {
			cardTitle: 'Não perca seus dados!',
			cardDescription: (
				<>
					Seu período de teste encerra <strong>amanhã</strong>
				</>
			),
			modalTitle: 'Você tem apenas 1 dia restante para o fim do seu período de teste gratuito do Kwik Scout',
			modalDescription: 'Assine agora mesmo e não perca seus dados',
		}
	}

	if (trialStatus.kind === 'critical') {
		return {
			cardTitle: 'Não perca seus dados!',
			cardDescription: `Seu período de teste encerra em ${trialStatus.daysRemaining} dias`,
			modalTitle: `Você tem apenas ${trialStatus.daysRemaining} dias restantes para o fim do seu período de teste gratuito do Kwik Scout`,
			modalDescription: 'Assine agora mesmo e não perca seus dados',
		}
	}

	return {
		cardTitle: 'Gostando do Kwik Scout?',
		cardDescription: `Seu período de teste encerra em ${trialStatus.daysRemaining} dias`,
		modalTitle: `Você tem apenas ${trialStatus.daysRemaining} dias restantes para o fim do seu período de teste gratuito do Kwik Scout`,
		modalDescription: 'Assine agora mesmo e não perca seus dados',
	}
}

const getBillingStatus = (enterprise) => {
	const planEndDate = getPlanEndDate(enterprise)
	const trialEndAt = planEndDate ? dayjs(planEndDate) : null

	if (isTrial(enterprise) && trialEndAt?.isValid() && trialEndAt.startOf('day').diff(dayjs().startOf('day'), 'day') >= 0) {
		return null
	}

	const status = String(enterprise?.charge_status || '').trim().toUpperCase()
	const endCharging = enterprise?.end_charging ? dayjs(enterprise.end_charging) : null
	const daysRemaining = endCharging?.isValid() ? Math.max(0, Math.ceil(endCharging.endOf('day').diff(dayjs(), 'day', true))) : null

	if (!status || status === 'COMPLIANT') {
		return null
	}

	if (status === 'NOTCOMPLIANT') {
		return {
			kind: 'not-compliant',
			message: 'Realize a sua primeira assinatura para ativar o Kwik Scout.',
			daysRemaining,
		}
	}

	if (status.startsWith('DEFAULTER')) {
		return {
			kind: 'defaulter',
			level: status,
			message: 'Seu pagamento falhou e seu acesso está limitado até a regularização.',
			daysRemaining,
		}
	}

	if (status === 'SCHEDULED_CANCEL') {
		return {
			kind: 'scheduled-cancel',
			message: 'Sua assinatura está agendada para cancelamento. Renove para manter o acesso.',
			daysRemaining,
		}
	}

	if (status === 'CANCELLED') {
		return {
			kind: 'cancelled',
			message: 'Sua assinatura foi cancelada. Para continuar usando, faça uma nova assinatura.',
			daysRemaining,
		}
	}

	return null
}

const getPendingInviteAccount = (accounts = []) => {
	return accounts.find((account) => {
		return Boolean(account?.email) && !account?.connection_validated && !account?.initial_connection && !isScoutAccountActive(account)
	}) || null
}

const buildInvitePendingModal = (account) => {
	if (!account) {
		return null
	}

	return {
		icon: <ScheduleOutlinedIcon />,
		contentTitle: (
			<span>
				O convite enviado para <span className="default-modal__highlight">{account.name || 'este contato'}</span> integrar seu WhatsApp ao Kwik Scout <span className="default-modal__highlight">ainda não foi aceito</span>
			</span>
		),
		contentDescription: <span>Peça para conferir o e-mail e seguir as instruções. É simples e muito rápido!</span>,
		primaryAction: {
			label: 'Deseja reenviar o convite?',
			type: 'renotifyInvite',
			accountId: account.id,
		},
	}
}

const buildInviteSentModal = (account) => {
	if (!account) {
		return null
	}

	return {
		icon: <MarkEmailReadOutlinedIcon />,
		contentTitle: (
			<>
				<span>
					O convite foi reenviado para <span className="default-modal__highlight">{account.name || 'este contato'}</span> no e-mail <span className="default-modal__highlight">{account.email || ''}</span>
				</span>
			</>
		),
		contentDescription: <span>Peça para conferir a caixa de entrada e seguir as instruções.</span>,
		primaryAction: {
			label: 'Acessar página inicial',
			type: 'closeModal',
		},
	}
}


const isAccountConnectedOnce = (account) => Boolean(account?.initial_connection || account?.connection_validated)

const buildTrialWarningModal = (trialStatus) => {
	const trialCopy = getTrialCopy(trialStatus)

	return {
		icon: <ScheduleOutlinedIcon />,
		contentTitle: (
			<span>
				Você tem{' '}
				<span className="default-modal__highlight">apenas {trialStatus.daysRemaining} dias restantes</span>
				 {' '}para o fim do seu período de teste gratuito do Kwik Scout
			</span>
		),
		contentDescription: <span>{trialCopy.modalDescription}</span>,
		primaryAction: {
			label: 'Conheça os planos e assine',
			type: 'openModal',
			modalKey: 'trial-plans',
		},
	}
}

const buildTrialCriticalModal = (trialStatus) => {
	const trialCopy = getTrialCopy(trialStatus)

	return {
		icon: <ScheduleOutlinedIcon />,
		contentTitle: (
			<span>
				Você tem{' '}
				<span className="default-modal__highlight default-modal__highlight--danger">apenas {trialStatus.daysRemaining} dias restantes</span>
				 {' '}para o fim do seu período de teste gratuito do Kwik Scout
			</span>
		),
		contentDescription: <span>{trialCopy.modalDescription}</span>,
		primaryAction: {
			label: 'Conheça os planos e assine',
			type: 'openModal',
			modalKey: 'trial-plans',
		},
	}
}

const buildTrialLastDayModal = (trialStatus) => {
	const trialCopy = getTrialCopy(trialStatus)

	return {
		icon: <ScheduleOutlinedIcon />,
		contentTitle: (
			<span>
				Você tem{' '}
				<span className="default-modal__highlight default-modal__highlight--danger">apenas 1 dia restante</span>
				 {' '}para o fim do seu período de teste gratuito do Kwik Scout
			</span>
		),
		contentDescription: <span>{trialCopy.modalDescription}</span>,
		primaryAction: {
			label: 'Conheça os planos e assine',
			type: 'openModal',
			modalKey: 'trial-plans',
		},
	}
}

const buildTrialExpiredModal = (greetingName) => ({
	icon: <AttachMoneyIcon />,
	contentTitle: <span className="default-modal__highlight default-modal__highlight--danger">Olá {greetingName}, o seu período de teste gratuito do Kwik Scout encerrou</span>,
	contentDescription: <span>Mas  não se preocupe, basta fazer sua assinatura para recuperar seu acesso e os dados de monitoramento</span>,
	primaryAction: {
		label: 'Conheça os planos e assine',
		type: 'openModal',
		modalKey: 'trial-plans',
	},
})

const buildCheckoutSuccessModal = (checkoutMode) => ({
	icon: <CheckCircleOutlineIcon />,
	contentTitle: <span className="default-modal__highlight">Compra concluída com sucesso</span>,
	contentDescription: (
		<span>
			{checkoutMode === 'upgrade'
				? 'Seu upgrade foi confirmado e o novo plano já está disponível no Kwik Scout.'
				: 'Sua assinatura foi confirmada e o plano já está disponível no Kwik Scout.'}
		</span>
	),
	primaryAction: {
		label: 'Acessar página inicial',
		type: 'closeModal',
	},
})

const buildCheckoutCancelModal = (checkoutMode) => ({
	icon: <InfoOutlinedIcon />,
	contentTitle: <span className="default-modal__highlight">Checkout  não finalizado</span>,
	contentDescription: (
		<span>
			{checkoutMode === 'upgrade'
				? 'O upgrade não foi concluído. Escolha novamente um plano acima do atual para continuar.'
				: 'A assinatura não foi concluída. Escolha novamente um plano para continuar.'}
		</span>
	),
	primaryAction: {
		label: checkoutMode === 'upgrade' ? 'Voltar para upgrades' : 'Ver planos novamente',
		type: 'openModal',
		modalKey: checkoutMode === 'upgrade' ? 'upgrade-plans' : 'trial-plans',
	},
})

const buildBillingModal = (billingStatus, greetingName, enterprise) => {
	if (!billingStatus) {
		return null
	}

	const isStripe = enterprise?.plan?.external_provider === 'STRIPE'

	const titleMap = {
		'not-compliant': 'Ative sua assinatura',
		defaulter: 'Pagamento em atraso',
		'scheduled-cancel': 'Assinatura agendada para cancelamento',
		cancelled: 'Assinatura cancelada',
	}

	const subtitleMap = {
		'not-compliant': 'Cadastre um método de pagamento para ativar o Kwik Scout.',
		defaulter: 'Regularize sua assinatura para retomar o acesso completo.',
		'scheduled-cancel': 'Em breve você perderá o acesso.',
		cancelled: 'Sua assinatura foi cancelada e sua conta será excluída. Assine um plano para reativar o Kwik Scout.',
	}

	let primaryAction = {
		label: 'Alterar assinatura',
		type: 'support',
	}

	if (isStripe) {
		if (billingStatus.kind === 'not-compliant' || billingStatus.kind === 'cancelled') {
			primaryAction = {
				label: 'Assinar',
				type: 'stripeCheckout',
			}
		} else if (billingStatus.kind === 'scheduled-cancel') {
			primaryAction = {
				label: 'Manter minha assinatura',
				type: 'stripeReactivate',
			}
		} else if (billingStatus.kind === 'defaulter') {
			primaryAction = {
				label: 'Realizar pagamento',
				type: 'stripePayOverdue',
			}
		} else {
			primaryAction = {
				label: 'Cancelar assinatura',
				type: 'stripeCancel',
			}
		}
	}

	return {
		icon: <AttachMoneyIcon />,
		contentTitle: <span className="default-modal__highlight">{titleMap[billingStatus.kind]}</span>,
		contentDescription: (
			<span>
				{subtitleMap[billingStatus.kind]}
			</span>
		),
		primaryAction,
	}
}

const buildTrialSidebarCard = (trialStatus) => {
	if (!trialStatus) {
		return null
	}

	const trialCopy = getTrialCopy(trialStatus)

	if (trialStatus.kind === 'expired') {
		return {
			visible: true,
			className: 'scout-panel--accent-danger',
			icon: <ErrorOutlineOutlinedIcon />,
			title: trialCopy.cardTitle,
			description: trialCopy.cardDescription,
			progress: 100,
			actionLabel: 'Assinar agora',
			dismissible: false,
			action: {
				type: 'openModal',
				modalKey: 'trial-plans',
			},
		}
	}

	if (trialStatus.kind === 'last-day') {
		return {
			visible: true,
			className: 'scout-panel--accent-danger',
			icon: <ErrorOutlineOutlinedIcon />,
			title: trialCopy.cardTitle,
			description: trialCopy.cardDescription,
			progress: trialStatus.progress,
			actionLabel: 'Assinar agora',
			dismissible: true,
			action: {
				type: 'openModal',
				modalKey: 'trial-plans',
			},
		}
	}

	if (trialStatus.kind === 'critical') {
		return {
			visible: true,
			className: 'scout-panel--accent-danger',
			icon: <ErrorOutlineOutlinedIcon />,
			title: trialCopy.cardTitle,
			description: trialCopy.cardDescription,
			progress: trialStatus.progress,
			actionLabel: 'Assinar agora',
			dismissible: true,
			action: {
				type: 'openModal',
				modalKey: 'trial-plans',
			},
		}
	}

	return {
		visible: true,
		className: 'scout-panel--accent-warning',
		icon: <CreditCardOutlinedIcon />,
		title: trialCopy.cardTitle,
		description: trialCopy.cardDescription,
		progress: trialStatus.progress,
		actionLabel: 'Assinar agora',
		dismissible: true,
		action: {
			type: 'openModal',
			modalKey: 'trial-plans',
		},
	}
}

const getBillingAction = (billingStatus, isStripe) => {
	if (!isStripe) {
		return { type: 'openModal', modalKey: 'trial-plans' }
	}

	switch (billingStatus?.kind) {
		case 'scheduled-cancel':
			return { type: 'stripeReactivate' }
		case 'defaulter':
			return { type: 'stripePayOverdue' }
		case 'not-compliant':
		case 'cancelled':
			return { type: 'stripeCheckout' }
		default:
			return { type: 'stripeCancel' }
	}
}

const buildBillingSidebarCard = (billingStatus, enterprise) => {
	if (!billingStatus) {
		return null
	}

	const isStripe = enterprise?.plan?.external_provider === 'STRIPE'

	const base = {
		visible: true,
		actionLabel: 'Ajustar assinatura',
		dismissible: false,
		action: getBillingAction(billingStatus, isStripe),
	}

	if (billingStatus.kind === 'not-compliant') {
		return {
			...base,
			className: 'scout-panel--accent-warning',
			icon: <ErrorOutlineOutlinedIcon />,
			title: 'Ainda não há assinatura ativa',
			description: 'Assine agora para ativar o Kwik Scout.',
			progress: 0,
		}
	}

	if (billingStatus.kind === 'defaulter') {
		return {
			...base,
			actionLabel: 'Realizar pagamento',
			className: 'scout-panel--accent-danger',
			icon: <ErrorOutlineOutlinedIcon />,
			title: 'Pagamento em atraso',
			description: 'Regularize para retomar o acesso completo.',
			progress: 100,
		}
	}

	if (billingStatus.kind === 'scheduled-cancel') {
		return {
			...base,
			actionLabel: 'Manter minha assinatura',
			className: 'scout-panel--accent-warning',
			icon: <ScheduleOutlinedIcon />,
			title: 'Cancelamento agendado',
			description: 'Em breve você perderá o acesso.',
			progress: getBillingCycleProgress(enterprise),
		}
	}

	if (billingStatus.kind === 'cancelled') {
		return {
			...base,
			className: 'scout-panel--accent-danger',
			icon: <ErrorOutlineOutlinedIcon />,
			title: 'Assinatura cancelada',
			description: 'Faça uma nova assinatura para continuar usando o Kwik Scout.',
			progress: 100,
		}
	}

	return null
}

const mergeAccountsById = (currentAccounts = [], nextAccounts = [], { reset = false } = {}) => {
	const mergedAccounts = reset ? [] : [...currentAccounts]
	const accountIndexes = new Map(mergedAccounts.map((account, index) => [String(account.id), index]))

	nextAccounts.forEach((account) => {
		const accountId = String(account?.id || '')
		if (!accountId) {
			return
		}

		if (accountIndexes.has(accountId)) {
			const accountIndex = accountIndexes.get(accountId)
			mergedAccounts[accountIndex] = {
				...mergedAccounts[accountIndex],
				...account,
			}
			return
		}

		accountIndexes.set(accountId, mergedAccounts.length)
		mergedAccounts.push(account)
	})

	return mergedAccounts
}

const Home = () => {
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const initialDateOptions = listScoutDateOptions()
	const initialSelectedPreset = searchParams.get('period') || getDefaultScoutDatePreset()
	const initialSelectedOption = initialDateOptions.find((option) => option.key === initialSelectedPreset) || initialDateOptions[0]
	const homeConfig = useMemo(() => getHomeDashboardConfig(), [])
	const { user } = getScoutContext()
	const { showSnackbar } = useSnackbar()
	const [accounts, setAccounts] = useState([])
	const [accountsTotal, setAccountsTotal] = useState(0)
	const [accountsPage, setAccountsPage] = useState(1)
	const [accountsHasMore, setAccountsHasMore] = useState(false)
	const [accountsLoadingMore, setAccountsLoadingMore] = useState(false)
	const [accountFilterBy, setAccountFilterBy] = useState('name')
	const [appliedAccountFilterBy, setAppliedAccountFilterBy] = useState('name')
	const [accountFilterText, setAccountFilterText] = useState('')
	const [debouncedAccountFilterText, setDebouncedAccountFilterText] = useState('')
	const [enterprise, setEnterprise] = useState(null)
	const [activeModalName, setActiveModalName] = useState('')
	const [selectedAccountId, setSelectedAccountId] = useState(searchParams.get('accountId') || '')
	const [dateValue, setDateValue] = useState(() => normalizeScoutDate(searchParams.get('date') || initialSelectedOption?.endDate || getDefaultScoutDatePresetValue()))
	const [selectedDatePreset, setSelectedDatePreset] = useState(initialSelectedPreset)
	const [searchValue, setSearchValue] = useState('')
	const [searchResultsOpen, setSearchResultsOpen] = useState(false)
	const [searchResultsLoading, setSearchResultsLoading] = useState(false)
	const [searchResults, setSearchResults] = useState([])
	const [searchFeedback, setSearchFeedback] = useState('')
	const [analytics, setAnalytics] = useState(null)
	const [accountsLoading, setAccountsLoading] = useState(true)
	const [dashboardLoading, setDashboardLoading] = useState(false)
	const [initialDataReady, setInitialDataReady] = useState(false)
	const [error, setError] = useState('')
	const [hasInitializedStatusModal, setHasInitializedStatusModal] = useState(false)
	const [sidebarCardDismissed, setSidebarCardDismissed] = useState(false)
	const [resentInviteAccount, setResentInviteAccount] = useState(null)
	const [pendingUpgradePlan, setPendingUpgradePlan] = useState(null)
	const [isChangingPlan, setIsChangingPlan] = useState(false)
	const [checkoutRedirectStatus, setCheckoutRedirectStatus] = useState('')
	const [checkoutRedirectMode, setCheckoutRedirectMode] = useState('subscribe')
	const [isDateMenuOpen, setIsDateMenuOpen] = useState(false)

	const [documentDialog, setDocumentDialog] = useState(null)
	const [documentType, setDocumentType] = useState("cpf")
	const [documentValue, setDocumentValue] = useState("")
	
	const [annualDiscount, setAnnualDiscount] = useState(true)

	const [showGreetingModal, setShowGreetingModal] = useState(false)
	const [showConfirmation, setShowConfirmation] = useState(null)
	
	const processedCheckoutRef = useRef('')
	const showSnackbarRef = useRef(showSnackbar)
	const dateDropdownRef = useRef(null)
	const searchDebounceTimerRef = useRef(null)
	const accountsRequestRef = useRef(0)
	const accountFilterTextRef = useRef(accountFilterText)
	const checkoutStatusParam = searchParams.get('stripe_checkout') || ''
	const checkoutModeParam = searchParams.get('checkout_mode') || ''
	const checkoutSessionIdParam = searchParams.get('session_id') || ''

	const mask = documentType === "cpf" ? CPF_MASK : CNPJ_MASK

	const executeSearch = async (query) => {
		if (!query || query.length < 3) {
			setSearchResults([])
			setSearchFeedback('')
			setSearchResultsOpen(false)
			return
		}

		setSearchResultsOpen(true)
		setSearchResultsLoading(true)
		setSearchFeedback('')

		try {
			const normalizedQuery = normalizeQueryText(query)
			const response = await searchScoutMessages({ text: normalizedQuery })
			const nextResults = Object.values(response || {})

			setSearchResults(nextResults)
			if (!nextResults.length) {
				setSearchFeedback(`Nenhuma mensagem com "${query}" encontrada.`)
			}
		} catch (searchError) {
			setSearchResults([])
			setSearchFeedback(searchError.message || 'Não foi possível buscar mensagens.')
		} finally {
			setSearchResultsLoading(false)
		}
	}

	useEffect(() => {
		if (searchDebounceTimerRef.current) {
			clearTimeout(searchDebounceTimerRef.current)
		}

		const trimmedSearchValue = String(searchValue || '').trim()

		if (trimmedSearchValue.length < 3) {
			setSearchResults([])
			setSearchFeedback('')
			setSearchResultsOpen(false)
			return
		}

		searchDebounceTimerRef.current = setTimeout(() => {
			executeSearch(trimmedSearchValue)
		}, 1000)

		return () => {
			if (searchDebounceTimerRef.current) {
				clearTimeout(searchDebounceTimerRef.current)
			}
		}
	}, [searchValue])

	useEffect(() => {
		showSnackbarRef.current = showSnackbar
	}, [showSnackbar])

	const handleAccountsRefresh = useCallback((nextAccounts) => {
		setError('')
		setAccounts(nextAccounts)
	}, [])

	useEffect(() => {
		if (!isDateMenuOpen) {
			return undefined
		}

		const handlePointerDown = (event) => {
			if (!dateDropdownRef.current?.contains(event.target)) {
				setIsDateMenuOpen(false)
			}
		}

		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				setIsDateMenuOpen(false)
			}
		}

		document.addEventListener('mousedown', handlePointerDown)
		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('mousedown', handlePointerDown)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isDateMenuOpen])

	useEffect(() => {
	    const greeting = searchParams.get("greeting")

	    if (greeting) {
	        setShowGreetingModal(greeting)

	        searchParams.delete("greeting")
	        setSearchParams(searchParams, { replace: true })
	    }
	}, [searchParams, setSearchParams])

	useEffect(() => {
		accountFilterTextRef.current = accountFilterText

		const debounceTimer = window.setTimeout(() => {
			setDebouncedAccountFilterText(accountFilterText.trim())
			setAppliedAccountFilterBy(accountFilterBy)
		}, 350)

		return () => window.clearTimeout(debounceTimer)
	}, [accountFilterText])

	const effectiveAccountFilterBy = debouncedAccountFilterText ? appliedAccountFilterBy : 'name'

	const loadAccountsPage = useCallback(async ({ page = 1, reset = false, showLoader = false } = {}) => {
		const queryFilter = debouncedAccountFilterText
		const queryFilterBy = effectiveAccountFilterBy
		const requestId = reset ? accountsRequestRef.current + 1 : accountsRequestRef.current
		if (reset) {
			accountsRequestRef.current = requestId
			setAccountsPage(1)
			setAccountsHasMore(false)
		}

		if (showLoader) {
			setAccountsLoading(true)
			setInitialDataReady((currentValue) => currentValue)
			setError('')
		} else {
			setAccountsLoadingMore(true)
		}

		try {
			const data = await listScoutAccountsPage({
				page,
				pageSize: DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE,
				filterBy: queryFilterBy,
				filter: queryFilter,
			})

			if (
				accountsRequestRef.current !== requestId
				|| (reset && accountFilterTextRef.current.trim() !== queryFilter)
			) {
				return data
			}

			setAccounts((currentAccounts) => mergeAccountsById(currentAccounts, data.results, { reset }))
			setAccountsTotal(data.count)
			setAccountsPage(data.page)
			setAccountsHasMore(data.hasMore)

			if (!data.results.length && reset) {
				setInitialDataReady(true)
			}

			return data
		} catch (nextError) {
			const errorMessage = nextError.message || 'Não foi possível carregar as contas.'
			setError(errorMessage)
			showSnackbarRef.current?.({ message: errorMessage, severity: 'error' })
			setInitialDataReady(true)
			throw nextError
		} finally {
			if (accountsRequestRef.current !== requestId) {
				return
			}

			if (showLoader) {
				setAccountsLoading(false)
			} else {
				setAccountsLoadingMore(false)
			}
		}
	}, [debouncedAccountFilterText, effectiveAccountFilterBy])

	useEffect(() => {
		let isMounted = true

		const loadAccounts = async () => {
			try {
				const [accountsResult, enterpriseResult] = await Promise.allSettled([
					loadAccountsPage({ page: 1, reset: true, showLoader: true }),
					getScoutEnterprise(),
				])

				if (!isMounted) {
					return
				}

				if (enterpriseResult.status === 'fulfilled') {
					setEnterprise(enterpriseResult.value)
				}

				if (accountsResult.status !== 'fulfilled') {
					throw accountsResult.reason
				}
			} catch (nextError) {
				if (isMounted && nextError?.message) {
					console.warn('Falha ao carregar números monitorados', nextError)
				}
			}
		}

		loadAccounts()

		return () => {
			isMounted = false
		}
	}, [loadAccountsPage])

	useEffect(() => {
		if (enterprise?.plan?.name !== "Scout Free")
			return
		
		handleConfiguredAction({
			type: "openModal",
			modalKey: "upgrade-plans"
		})
	}, [enterprise])

	useHomeAccountsPolling({
		accounts,
		accountsLoading,
		onRefresh: handleAccountsRefresh,
		fetchAccounts: async () => {
			const requestId = accountsRequestRef.current
			const data = await listScoutAccountsPage({
				page: 1,
				pageSize: Math.max(accounts.length, DEFAULT_SCOUT_ACCOUNTS_PAGE_SIZE),
				filterBy: effectiveAccountFilterBy,
				filter: debouncedAccountFilterText,
			})

			if (accountsRequestRef.current !== requestId) {
				return accounts
			}

			if (accountFilterTextRef.current.trim() !== debouncedAccountFilterText) {
				return accounts
			}

			setAccountsTotal(data.count)
			setAccountsHasMore(data.hasMore)
			return data.results
		},
		interval: 10000,
	})

	useEffect(() => {
		if (accountsLoading) {
			return
		}

		const hasMatchingAccount = !selectedAccountId || accounts.some((account) => String(account.id) === String(selectedAccountId))

		if (!hasMatchingAccount) {
			setSelectedAccountId('')
		}
	}, [accounts, accountsLoading, selectedAccountId])

	useEffect(() => {
		const normalizedDate = normalizeScoutDate(dateValue)

		if (normalizedDate !== dateValue) {
			setDateValue(normalizedDate)
		}
	}, [dateValue])

	const baseDateOptions = useMemo(() => listScoutDateOptions(), [])

	const selectedDateOption = useMemo(() => {
		return baseDateOptions.find((option) => option.key === selectedDatePreset) || baseDateOptions[0] || null
	}, [baseDateOptions, selectedDatePreset])

	useEffect(() => {
		if (selectedDateOption?.endDate && selectedDateOption.endDate !== dateValue) {
			setDateValue(selectedDateOption.endDate)
		}
	}, [dateValue, selectedDateOption])

	const selectedDateRange = useMemo(() => {
		if (selectedDateOption) {
			return {
				startDate: selectedDateOption.startDate || selectedDateOption.value || dateValue,
				endDate: selectedDateOption.endDate || selectedDateOption.value || dateValue,
			}
		}

		return {
			startDate: dateValue,
			endDate: dateValue,
		}
	}, [dateValue, selectedDateOption])

	const dateOptions = useMemo(() => baseDateOptions, [baseDateOptions])

	const selectedDateLabel = useMemo(() => {
		return selectedDateOption?.label || formatDateLabel(dateValue)
	}, [dateValue, selectedDateOption])

	useEffect(() => {
		if (!accounts.length) {
			setAnalytics(null)
			setDashboardLoading(false)
			setError('')
			setInitialDataReady(true)
			return
		}

		let isMounted = true
		setDashboardLoading(true)
		setError('')

		getScoutAccountAnalytics({
			accountId: selectedAccountId || null,
			dateStart: selectedDateRange.startDate,
			dateEnd: selectedDateRange.endDate,
		})
			.then((nextAnalytics) => {
				if (!isMounted) {
					return
				}

				setAnalytics(nextAnalytics)
			})
			.catch((nextError) => {
				if (isMounted) {
					const errorMessage = nextError.message || 'Não foi possível carregar os dados do dashboard.'
					setError(errorMessage)
					showSnackbar({ message: errorMessage, severity: 'error' })
				}
			})
			.finally(() => {
				if (isMounted) {
					setDashboardLoading(false)
					setInitialDataReady(true)
				}
			})

		return () => {
			isMounted = false
		}
	}, [accounts, selectedAccountId, selectedDateRange.endDate, selectedDateRange.startDate, showSnackbar])

	useEffect(() => {
	    const currentParams = new URLSearchParams(searchParams)

	    if (selectedAccountId)
	        currentParams.set("accountId", selectedAccountId)
	    else
	        currentParams.delete("accountId")

	    if (dateValue)
	        currentParams.set("date", dateValue)
	    else
	        currentParams.delete("date")

	    if (selectedDatePreset)
	        currentParams.set("period", selectedDatePreset)
	    else
	        currentParams.delete("period")

	    if (checkoutStatusParam)
	        currentParams.set("stripe_checkout", checkoutStatusParam)

	    if (checkoutModeParam)
	        currentParams.set("checkout_mode", checkoutModeParam)

	    if (checkoutSessionIdParam)
	        currentParams.set("session_id", checkoutSessionIdParam)

	    setSearchParams(currentParams, { replace: true })
	}, [
	    checkoutModeParam,
	    checkoutSessionIdParam,
	    checkoutStatusParam,
	    dateValue,
	    selectedAccountId,
	    selectedDatePreset,
	    setSearchParams,
	    searchParams
	])

	const accountsWithDecorators = useMemo(() => {
		return accounts.map((account) => ({
			...account,
			decorator: getHomeAccountDecorator(account),
		}))
	}, [accounts])

	const firstConnectionAccount = useMemo(() => {
		if (!accountsWithDecorators.length) {
			return null
		}

		const hasConnectedOnce = Boolean(enterprise?.scout_first_connection_at) || accountsWithDecorators.some(isAccountConnectedOnce)

		if (hasConnectedOnce) {
			return null
		}

		return accountsWithDecorators.find((account) => !isAccountConnectedOnce(account)) || accountsWithDecorators[0]
	}, [accountsWithDecorators, enterprise?.scout_first_connection_at])

	const refreshScoutAccounts = useCallback(async () => {
		try {
			const [accountsResult, enterpriseResult] = await Promise.allSettled([
				loadAccountsPage({ page: 1, reset: true, showLoader: true }),
				getScoutEnterprise(),
			])

			if (enterpriseResult.status === 'fulfilled') {
				setEnterprise(enterpriseResult.value)
			}

			if (accountsResult.status !== 'fulfilled') {
				throw accountsResult.reason
			}
		} catch (error) {
			showSnackbar({ message: error.message || 'Não foi possível atualizar os números monitorados.', severity: 'error' })
		}
	}, [loadAccountsPage, showSnackbar])

	const loadMoreAccounts = useCallback(() => {
		if (accountsLoading || accountsLoadingMore || !accountsHasMore) {
			return
		}

		loadAccountsPage({ page: accountsPage + 1, reset: false })
			.catch(() => {})
	}, [accountsHasMore, accountsLoading, accountsLoadingMore, accountsPage, loadAccountsPage])

	const handleAccountsListScroll = useCallback((event) => {
		const element = event.currentTarget
		const distanceFromBottom = element.scrollHeight - (element.scrollTop + element.clientHeight)

		if (distanceFromBottom <= 160) {
			loadMoreAccounts()
		}
	}, [loadMoreAccounts])

	const handleSendConnectionQrEmail = useCallback(async (account, email) => {
		await renotifyScoutAccount({ accountId: account?.id, email })
	}, [])

	const selectedAccount = useMemo(() => {
		return accountsWithDecorators.find((account) => String(account.id) === String(selectedAccountId)) || null
	}, [accountsWithDecorators, selectedAccountId])

	const accountByInstanceName = useMemo(() => {
		return accountsWithDecorators.reduce((lookup, account) => {
			if (account?.instance_name) {
				lookup[account.instance_name] = account
			}

			return lookup
		}, {})
	}, [accountsWithDecorators])

	const normalizedSearchResults = useMemo(() => {
		return searchResults.map((entry, index) => {
			const account = accountByInstanceName[entry?.owner] || null
			const conversationId = entry?.info?.id || ''

			return {
				id: `${conversationId || entry?.owner || 'search'}-${index}`,
				conversationId,
				conversationName: entry?.conversationName || 'Conversa monitorada',
				account,
				messages: Array.isArray(entry?.messages) ? entry.messages : [],
			}
		})
	}, [accountByInstanceName, searchResults])

	const activeAccounts = useMemo(() => {
		return accountsTotal || accountsWithDecorators.length
	}, [accountsTotal, accountsWithDecorators.length])
	const shouldShowEmptyNumbersGate = initialDataReady && !accountsLoading && !debouncedAccountFilterText && activeAccounts === 0
	const shouldShowAccountFilter = activeAccounts > 3 || Boolean(accountFilterText.trim())
	const shouldShowAccountsEmptyState = !accountsLoading && !accountsWithDecorators.length
	const isAccountListRefreshing = accountsLoading && initialDataReady && accountsWithDecorators.length > 0

	const preferMockAnalytics = Boolean(homeConfig?.dataSource?.useMockAnalytics)
	const metricsConfig = useMemo(() => homeConfig?.metrics || {}, [homeConfig])
	
	const [stripePlanBySlug, setStripePlanBySlug] = useState({})

	useEffect(() => {
		let isMounted = true

		const loadStripePlans = async () => {
			try {
				const loadedPlans = await listStripePlans()
				if (!isMounted || !Array.isArray(loadedPlans)) {
					return
				}

				const nextPlanIds = loadedPlans.reduce((lookup, stripePlan) => {
					const slug = resolveScoutPlanSlug(stripePlan?.name)

					if (!TRACKED_SCOUT_PLAN_SLUGS.has(slug) || !stripePlan?.id) {
						return lookup
					}

					lookup[slug] = {
						id: stripePlan.id,
						slug: slug,
						monthlyValue: stripePlan.monthly_value,
						discountValue: stripePlan.discount_value,
						discountBadge: stripePlan.discount_badge,
					}
					return lookup
				}, {})

				setStripePlanBySlug(nextPlanIds)
			} catch (err) {
				console.warn('Falha ao carregar planos Stripe', err)
			}
		}

		loadStripePlans()

		return () => {
			isMounted = false
		}
	}, [])

	const subscription = useMemo(() => {
		const livePlanSlug = slugifyPlanName(enterprise?.plan?.name)
		const activePlanSlug = isTrial(enterprise) ? '' : livePlanSlug

		if (!enterprise)
			return

		return {
			activePlanSlug,
			isTrial: isTrial(enterprise),
			isFreePlan: isFreePlan(enterprise),
			scoutLicenses: enterprise.platforms?.scout_licenses ?? null,
			hasAnnualDiscout: Boolean(enterprise.external_coupon_id)
		}
	}, [enterprise])
	
	const trialStatus = useMemo(() => getTrialStatus(enterprise), [enterprise])
	const billingStatus = useMemo(() => getBillingStatus(enterprise), [enterprise])
	
	const isTrialExpired = trialStatus?.kind === 'expired'
	const isBlocked = isAccountBlocked(enterprise)

	const hasActiveSubscription = Boolean(subscription?.activePlanSlug)
	
	const currentSubscriptionPlan = useMemo(() => {
		if (!subscription?.activePlanSlug) {
			return null
		}

		const plan = SCOUT_PAYMENT_PLANS.find((plan) => resolveScoutPlanSlug(plan.slug || plan.name || '') === resolveScoutPlanSlug(subscription?.activePlanSlug)) || null

		return {
			...plan,
			price: (enterprise.external_monthly_price) ? enterprise.external_monthly_price : plan.price
		}
	}, [subscription?.activePlanSlug, enterprise])
	
	const createModalAction = (label, onClick) => {
		return (
			<button type="button" className="scout-primary-button scout-primary-button--home" onClick={onClick}>
				{label}
			</button>
		)
	}
	
	const handleCloseActiveModal = useCallback(() => {
		setActiveModalName('')
		setPendingUpgradePlan(null)
		setIsChangingPlan(false)
	}, [])
	
	const handleConfirmPlanChange = useCallback(async (withAnnualDiscount=false) => {
		const planId = pendingUpgradePlan?.planId || pendingUpgradePlan?.id || null

		if (!planId || isChangingPlan) {
			return
		}

		setIsChangingPlan(true)

		try {
			loader.on()
			await changeStripePlan({ planId, withAnnualDiscount })
			const url = new URL(window.location.href)
		    url.searchParams.set("greeting", "upgrade_success")
		    window.location.replace(url.toString())
		} catch (error) {
			console.error(error)
			if (error?.message?.toLowerCase().includes('autenticado') || error?.message?.includes('401')) {
				navigate('/login')
				return
			}

			showSnackbar({ message: 'Não foi possível alterar o plano agora. Tente novamente em alguns instantes.', severity: 'error' })
			setIsChangingPlan(false)
		}
	}, [isChangingPlan, navigate, pendingUpgradePlan, showSnackbar])
	
	const modalState = useMemo(() => {
		const pendingInviteAccount = getPendingInviteAccount(accountsWithDecorators)
		const activeAccountsCount = accountsWithDecorators.filter((account) => isScoutAccountActive(account)).length

		const invitePendingModal = buildInvitePendingModal(pendingInviteAccount)
		const inviteSentModal = buildInviteSentModal(resentInviteAccount || pendingInviteAccount)
		const billingModal = buildBillingModal(billingStatus, user?.first_name || user?.username || homeConfig?.hero?.fallbackFirstName || 'Eduardo', enterprise)

		let initialModalKey = ''

		if (!firstConnectionAccount && !shouldShowEmptyNumbersGate) {
			if (pendingInviteAccount && activeAccountsCount === 0) {
				initialModalKey = 'invite-pending'
			}

			if (initialModalKey !== 'invite-pending' && trialStatus?.kind === 'warning') {
				initialModalKey = 'trial-warning'
			}

			if (initialModalKey !== 'invite-pending' && trialStatus?.kind === 'critical') {
				initialModalKey = 'trial-critical'
			}

			if (trialStatus?.kind === 'last-day') {
				initialModalKey = 'trial-last-day'
			}

			if (trialStatus?.kind === 'expired') {
				initialModalKey = 'trial-expired'
			}

			if (!initialModalKey && billingStatus) {
				initialModalKey = 'billing'
			}

			if (!initialModalKey && !trialStatus && pendingInviteAccount) {
				initialModalKey = 'invite-pending'
			}
		}

		const sidebarCard = buildTrialSidebarCard(trialStatus) || buildBillingSidebarCard(billingStatus, enterprise) || (
			hasActiveSubscription
				? {
					visible: true,
					className: 'scout-panel--accent-growth',
					icon: <NorthEastIcon />,
					title: 'Sua operação  cresceu?',
					description: 'Expanda a capacidade de monitoramento',
					progress: 0,
					actionLabel: 'Fazer upgrade de plano',
					dismissible: true,
					action: {
						type: 'openModal',
						modalKey: 'upgrade-plans',
					},
				}
				: null
		)

		return {
			initialModalKey,
			invitePendingModal,
			inviteSentModal,
			billingModal,
			trialWarningModal: trialStatus?.kind === 'warning' ? buildTrialWarningModal(trialStatus) : null,
			trialCriticalModal: trialStatus?.kind === 'critical' ? buildTrialCriticalModal(trialStatus) : null,
			trialLastDayModal: trialStatus?.kind === 'last-day' ? buildTrialLastDayModal(trialStatus) : null,
			trialExpiredModal: trialStatus?.kind === 'expired' ? buildTrialExpiredModal(user?.first_name || user?.username || homeConfig?.hero?.fallbackFirstName || 'Eduardo') : null,
			checkoutSuccessModal: checkoutRedirectStatus === 'success' ? buildCheckoutSuccessModal(checkoutRedirectMode) : null,
			checkoutCancelModal: checkoutRedirectStatus === 'cancel' ? buildCheckoutCancelModal(checkoutRedirectMode) : null,
			sidebarCard,
		}
	}, [accountsWithDecorators, checkoutRedirectMode, checkoutRedirectStatus, enterprise, firstConnectionAccount, hasActiveSubscription, homeConfig, resentInviteAccount, trialStatus, user, billingStatus, shouldShowEmptyNumbersGate])

	const sidebarCard = modalState.sidebarCard

	const activeModal = useMemo(() => {
		if (activeModalName === 'checkout-success') {
			return modalState.checkoutSuccessModal ? {
				...modalState.checkoutSuccessModal,
				action: createModalAction(
					modalState.checkoutSuccessModal.primaryAction?.label || 'Acessar página inicial',
					() => setActiveModalName(''),
				),
			} : null
		}

		if (activeModalName === 'checkout-cancel') {
			return modalState.checkoutCancelModal ? {
				...modalState.checkoutCancelModal,
				action: createModalAction(
					modalState.checkoutCancelModal.primaryAction?.label || 'Ver planos novamente',
					() => setActiveModalName(modalState.checkoutCancelModal.primaryAction?.modalKey || 'trial-plans'),
				),
			} : null
		}

		if (activeModalName === 'invite-pending') {
			return modalState.invitePendingModal ? {
				...modalState.invitePendingModal,
				action: createModalAction('Deseja reenviar o convite?', async () => {
					try {
						loader.on()
						await renotifyScoutAccount({ accountId: modalState.invitePendingModal.primaryAction?.accountId })
						showSnackbar({ message: "Convite reenviado com sucesso.", severity: "success" })
						setActiveModalName("")
						loader.off()
					} catch (nextError) {
						const errorMessage = nextError.message || 'Não foi possível reenviar o convite.'
						setError(errorMessage)
						showSnackbar({ message: errorMessage, severity: 'error' })
					}
				}),
			} : null
		}

		if (activeModalName === 'invite-sent') {
			return modalState.inviteSentModal ? {
				...modalState.inviteSentModal,
				action: createModalAction('Acessar página inicial', () => setActiveModalName('')),
			} : null
		}

		if (activeModalName === 'trial-warning') {
			return modalState.trialWarningModal ? {
				...modalState.trialWarningModal,
				action: createModalAction('Conheça os planos e assine', () => setActiveModalName('trial-plans')),
			} : null
		}

		if (activeModalName === 'trial-critical') {
			return modalState.trialCriticalModal ? {
				...modalState.trialCriticalModal,
				action: createModalAction('Conheça os planos e assine', () => setActiveModalName('trial-plans')),
			} : null
		}

		if (activeModalName === 'trial-last-day') {
			return modalState.trialLastDayModal ? {
				...modalState.trialLastDayModal,
				action: createModalAction('Conheça os planos e assine', () => setActiveModalName('trial-plans')),
			} : null
		}

		if (activeModalName === 'trial-expired') {
			return modalState.trialExpiredModal ? {
				...modalState.trialExpiredModal,
				action: createModalAction('Conheça os planos e assine', () => setActiveModalName('trial-plans')),
			} : null
		}

		// Deprecated !! It seems to redundant to have the dialog and the left floating content with the same information
		// if (activeModalName === 'billing') {
		// 	return modalState.billingModal ? {
		// 		...modalState.billingModal,
		// 		action: createModalAction(modalState.billingModal?.primaryAction?.label, () => {}),
		// 	} : null
		// }

		if (activeModalName === 'trial-plans' || activeModalName === 'upgrade-plans') {
			return {
				title: activeModalName === 'upgrade-plans' ? 'Faça o upgrade do seu plano' : 'Assine o Kwik Scout',
				subtitle: 'Conheça nossos planos',
			}
		}

		if (activeModalName === 'confirm-upgrade') {
			return pendingUpgradePlan ? {
				icon: <AttachMoneyIcon />,
				title: 'Confirmar upgrade',
				contentTitle: `Deseja fazer o upgrade para o plano ${pendingUpgradePlan.name}?`,
				contentDescription: [
					currentSubscriptionPlan?.price && pendingUpgradePlan?.price
						? `Sua mensalidade será alterada de ${formatBRL(currentSubscriptionPlan.price)} para ${formatBRL(pendingUpgradePlan.price)}.`
						: 'Seu plano será alterado imediatamente.',
				],
				action: (
					<button type="button" className="scout-primary-button scout-primary-button--home" onClick={() => handleConfirmPlanChange(annualDiscount)} disabled={isChangingPlan}>
						{isChangingPlan ? 'Alterando plano...' : 'Confirmar upgrade'}
					</button>
				),
			} : null
		}

		return null
	}, [activeModalName, currentSubscriptionPlan?.price, handleConfirmPlanChange, isChangingPlan, modalState, pendingUpgradePlan, showSnackbar, annualDiscount])

	const modalPlans = useMemo(() => {
		const orderedPlans = [...SCOUT_PAYMENT_PLANS].sort((first, second) => Number(first.rank || 0) - Number(second.rank || 0))

		if (subscription && ! subscription.isTrial && ! subscription.isFreePlan) {
			setAnnualDiscount(subscription.hasAnnualDiscout)	
		}

		const normalizedPlans = orderedPlans.map((plan) => {
			return {
				...plan,
				...resolveCheckoutPlan(plan, stripePlanBySlug)
			}
		})

		const subscribedPlan = normalizedPlans.find((plan) => 
			subscription?.activePlanSlug.endsWith(plan.slug)
		)
		const subscribedPlanRank = subscribedPlan?.rank ?? 0
		const availableUpgradePlans = normalizedPlans.filter((plan) => canRenderUpgradePlan(plan, subscribedPlanRank))

		if (activeModalName !== 'upgrade-plans' || !subscription?.activePlanSlug) {
			if (subscription && subscription.isTrial && ! isTrialExpired)
				return normalizedPlans.filter((plan) => plan.slug !== "free")
			return normalizedPlans
		}

		const currentPlanSlug = resolveScoutPlanSlug(subscription?.activePlanSlug || '')
		const currentPlan = availableUpgradePlans.find((plan) => resolveScoutPlanSlug(plan.slug || plan.name || '') === currentPlanSlug)

		if (!currentPlan) {
			return availableUpgradePlans
		}

		return availableUpgradePlans.filter((plan) => Number(plan.rank || 0) > Number(currentPlan.rank || 0))
	}, [activeModalName, stripePlanBySlug, subscription, isTrialExpired])
	
	const licensesTotal = resolveHomeValue({
		liveValue: getMaxLicenses(enterprise),
		mockValue: metricsConfig?.licenses?.total,
		preferMock: false,
		fallbackValue: 0,
	})
	
	const liveMessageCount = analytics ? (analytics?.conversation_messages || 0) + (analytics?.group_messages || 0) : null
	
	const groupsValue = resolveHomeValue({
		liveValue: analytics?.groups,
		mockValue: metricsConfig?.groups?.value,
		preferMock: preferMockAnalytics,
		fallbackValue: 0,
	})
	const conversationsValue = resolveHomeValue({
		liveValue: analytics?.conversations,
		mockValue: metricsConfig?.conversations?.value,
		preferMock: preferMockAnalytics,
		fallbackValue: 0,
	})
	const messagesValue = resolveHomeValue({
		liveValue: liveMessageCount,
		mockValue: metricsConfig?.messages?.value,
		preferMock: preferMockAnalytics,
		fallbackValue: 0,
	})

	const greetingSubtitle = useMemo(() => {
		if (!initialDataReady) {
			return homeConfig?.hero?.loadingSubtitle || 'Carregando dados do Scout...'
		}

		if (!accountsWithDecorators.length) {
			return homeConfig?.hero?.emptySubtitle || 'Você ainda  não possui nenhum número ativo'
		}

		if (messagesValue > 0) {
			return String(homeConfig?.hero?.messageSubtitleTemplate || 'Você tem {count} mensagens monitoradas')
				.replace('{count}', String(messagesValue))
				.replace('{date}', selectedDateLabel)
		}

		return homeConfig?.hero?.noMessagesSubtitle || "Clique em um dos números abaixo para ver conversas e métricas específicas"
	}, [accountsWithDecorators.length, homeConfig, initialDataReady, messagesValue, selectedDateLabel])

	const showInitialLoading = !initialDataReady && !error
	const showRefreshingState = initialDataReady && dashboardLoading

	useEffect(() => {
		if (!checkoutStatusParam) {
			return
		}

		const checkoutKey = [checkoutStatusParam, checkoutModeParam || 'subscribe', checkoutSessionIdParam].join(':')
		if (processedCheckoutRef.current === checkoutKey) {
			return
		}

		processedCheckoutRef.current = checkoutKey

		const checkoutMode = checkoutModeParam || 'subscribe'
		setCheckoutRedirectStatus(checkoutStatusParam)
		setCheckoutRedirectMode(checkoutMode)
		setHasInitializedStatusModal(true)
		setActiveModalName(checkoutStatusParam === 'success' ? 'checkout-success' : 'checkout-cancel')
		showSnackbarRef.current({
			message: checkoutStatusParam === 'success'
				? checkoutMode === 'upgrade'
					? 'Upgrade realizado com sucesso. Seu novo plano já está ativo.'
					: 'Assinatura realizada com sucesso. Seu plano já está ativo.'
				: checkoutMode === 'upgrade'
					? 'Upgrade cancelado. Escolha novamente um plano para continuar.'
					: 'Assinatura cancelada. Escolha novamente um plano para continuar.',
			severity: checkoutStatusParam === 'success' ? 'success' : 'info',
		})

		const nextParams = new URLSearchParams(searchParams)
		nextParams.delete('stripe_checkout')
		nextParams.delete('checkout_mode')
		nextParams.delete('session_id')
		setSearchParams(nextParams, { replace: true })
	}, [checkoutModeParam, checkoutSessionIdParam, checkoutStatusParam, searchParams, setSearchParams])

	useEffect(() => {
		if (accountsLoading || hasInitializedStatusModal) {
			return
		}

		if (modalState.initialModalKey) {
			setActiveModalName(modalState.initialModalKey)
		}

		setHasInitializedStatusModal(true)
	}, [accountsLoading, hasInitializedStatusModal, modalState.initialModalKey])

	const handleOpenConversations = (conversationId, accountId = selectedAccountId) => {
		if (isTrialExpired) {
			setActiveModalName('trial-expired')
			showSnackbar({ message: 'Seu período de teste expirou. Assine para recuperar o acesso ao Kwik Scout.', severity: 'warning' })
			return
		}

		const resolvedAccountId = accountId || getPreferredAccount(accountsWithDecorators)?.id

		if (!resolvedAccountId) {
			return
		}

		const params = new URLSearchParams()
		params.set('accountId', String(resolvedAccountId))
		params.set('date', dateValue)
		params.set('period', selectedDatePreset)

		if (conversationId) {
			params.set('conversationId', conversationId)
		}

		navigate(`/conversas?${params.toString()}`)
	}

	const hydrateSelectedAccount = useCallback(async (account) => {
		try {
			const liveAccount = await getScoutAccountDetails({ accountId: account?.id, live: true })
			if (!liveAccount?.id) {
				return
			}

			setAccounts((currentAccounts) => currentAccounts.map((currentAccount) => {
				return String(currentAccount.id) === String(liveAccount.id)
					? { ...currentAccount, ...liveAccount }
					: currentAccount
			}))
		} catch (error) {
			console.warn('Falha ao atualizar dados do número Scout', error)
		}
	}, [])

	const handleAccountClick = (account) => {
		if (isBlocked) {
			setActiveModalName(isTrialExpired ? 'trial-expired' : 'billing')
			return
		}

		const nextAccountId = String(selectedAccountId) === String(account.id) ? '' : String(account.id)
		setSelectedAccountId(nextAccountId)

		if (nextAccountId) {
			hydrateSelectedAccount(account)
		}
	}

	const handleCloseSearchResults = () => {
		setSearchResultsOpen(false)
		setSearchResults([])
		setSearchFeedback('')
	}

	const handleSearchSubmit = async (event) => {
		event?.preventDefault?.()

		if (searchDebounceTimerRef.current) {
			clearTimeout(searchDebounceTimerRef.current)
			searchDebounceTimerRef.current = null
		}

		const query = searchValue.trim()

		if (query.length < 3) {
			showSnackbar({ message: 'Para buscar, digite pelo menos 3 caracteres.', severity: 'warning' })
			return
		}

		await executeSearch(query)
	}

	const handleOpenSearchConversation = (result, message = null) => {
		if (!result?.account?.id || !result?.conversationId) {
			showSnackbar({ message: 'Não foi possível abrir a conversa a partir deste resultado.', severity: 'warning' })
			return
		}

		const params = new URLSearchParams()
		params.set('accountId', String(result.account.id))
		params.set('conversationId', String(result.conversationId))
		params.set('date', dateValue)

		const anchorId = message?.id || message?.chatMessageId || message?.messageId
		if (anchorId) {
			params.set('anchorMessageId', String(anchorId))
		}

		navigate(`/conversas?${params.toString()}`)
		setSearchResultsOpen(false)
	}

	const handleAccountOpen = (account) => {
		const nextAccountId = String(account.id)

		if (isBlocked) {
			setActiveModalName(isTrialExpired ? 'trial-expired' : 'billing')
			return
		}

		handleOpenConversations(undefined, nextAccountId)
	}

	const handleConfiguredAction = async (action) => {
		if (!action?.type) {
			return
		}

		if (action.type === 'openModal' && action.modalKey) {
			setActiveModalName(action.modalKey)
			return
		}

		if (action.type === 'closeModal') {
			setActiveModalName('')
			return
		}

		if (action.type === 'navigate' && action.path) {
			setActiveModalName('')
			navigate(action.path)
			return
		}

		if (action.type === 'support' || action.type === 'mailto') {
			setActiveModalName('')
			openSupportForm()
			return
		}

		if (action.type === 'renotifyInvite' && action.accountId) {
			const targetAccount = accountsWithDecorators.find((account) => String(account.id) === String(action.accountId)) || null

			try {
				await renotifyScoutAccount({ accountId: action.accountId })
				setResentInviteAccount(targetAccount)
				setActiveModalName('invite-sent')
			} catch (nextError) {
				const errorMessage = nextError.message || 'Não foi possível reenviar o convite.'
				setError(errorMessage)
				showSnackbar({ message: errorMessage, severity: 'error' })
			}

			return
		}

		if (action.type === 'stripeCheckout') {
			setActiveModalName('trial-plans')
			return
		}

		if (action.type === 'stripeCancel') {
			try {
				loader.on()
				await cancelStripeSubscription()
				const url = new URL(window.location.href)
			    url.searchParams.set("greeting", "subscription_cancelled")
			    window.location.replace(url.toString())
			} catch (nextError) {
				console.error(nextError)
				showSnackbar({ message: 'Falha ao cancelar assinatura. Tente novamente ou contate nosso time de suporte.', severity: 'error' })
			} finally {
				loader.off()
			}
			return
		}

		if (action.type === "stripePayOverdue") {
			setShowConfirmation({
				subtitle: "Vamos realizar uma cobrança no seu cartão de crédito para regularizar sua assinatura. Deseja continuar com o pagamento?",
				action: async () => {
					try {
						loader.on()
						await payOverdue()
						const url = new URL(window.location.href)
					    url.searchParams.set("greeting", "subscription_payed_overdue")
					    window.location.replace(url.toString())
					} catch (nextError) {
						console.error(nextError)
						showSnackbar({ message: "Falha ao regularizar a assinatura. Tente novamente ou contate nosso time de suporte.", severity: "error" })
					} finally {
						loader.off()
					}
				}
			})
			return
		}

		if (action.type === 'stripeReactivate') {
			setShowConfirmation({
				subtitle: "Tem certeza que deseja manter sua assinatura?",
				action: async () => {
					try {
						loader.on()
						await reactivateStripeSubscription()
						const url = new URL(window.location.href)
					    url.searchParams.set("greeting", "subscription_reactivated")
					    window.location.replace(url.toString())
					} catch (nextError) {
						console.error(nextError)
						showSnackbar({ message: "Falha ao reativar assinatura. Tente novamente ou contate nosso time de suporte.", severity: "error" })
					} finally {
						loader.off()
					}
				}
			})
			return
		}

		if (action.type === 'openConversations') {
			setActiveModalName('')
			handleOpenConversations()
		}
	}

	const handleCheckoutPlan = async (plan, skipDocument=false, withAnnualDiscount=false) => {
		if (!plan) {
			return
		}

		if (plan?.contactOnly) {
			openSupportForm()
			return
		}
		
		const selectedPlan = resolveCheckoutPlan(plan, stripePlanBySlug)
		const planId = selectedPlan?.id

		if (selectedPlan?.slug === "free") {
			setShowConfirmation({
				subtitle: "Tem certeza que deseja ativar o plano Free?",
				action: async () => {
					try {
						loader.on()
						await activateSubscription(planId)
						const url = new URL(window.location.href)
					    url.searchParams.set("greeting", "activation_success")
					    window.location.replace(url.toString())
					} catch (nextError) {
						console.error(nextError)
						showSnackbar({
							message: "Falha ao ativar o plano Free. Tente novamente ou contate nosso time de suporte.",
							severity: "error"
						})
					} finally {
						loader.off()
					}
				}
			})
			return
		}

		if (!planId) {
			console.warn('Não foi possível determinar planId para checkout', plan)
			showSnackbar({ message: 'Não foi possível determinar o plano para pagamento. Tente novamente ou contate o nosso suporte.', severity: 'warning' })
			return
		}

		if (!isFreePlan(enterprise) && activeModalName === 'upgrade-plans') {
			setPendingUpgradePlan({
				...plan,
				price: (withAnnualDiscount) ? plan.monthlyValue - plan.discountValue : plan.monthlyValue
			})
			setActiveModalName('confirm-upgrade')
			return
		}

		if (!enterprise?.cnpj && !skipDocument) {
			setDocumentDialog({ open: true, plan })
			return
		}

		try {
			loader.on()
			const response = await checkoutStripePlan({
				planId,
				checkoutMode: activeModalName === 'upgrade-plans' ? 'upgrade' : 'subscribe',
				withAnnualDiscount: withAnnualDiscount
			})
			loader.off()
			const redirectUrl = response?.url || response?.checkout_url || response?.session_url || response?.redirect_url
			if (redirectUrl) {
				window.location.assign(redirectUrl)
			}
		} catch (error) {
			console.error(error)
			loader.off()
			if (error?.message?.toLowerCase().includes('autenticado') || error?.message?.includes('401')) {
				navigate('/login')
			} else {
				showSnackbar({ message: 'Não foi possível iniciar o checkout. Tente novamente em alguns instantes.', severity: 'error' })
			}
		}
	}

	const greetingName = user?.first_name || user?.username || homeConfig?.hero?.fallbackFirstName || 'Eduardo'
	const shouldShowDashboardChrome = !shouldShowEmptyNumbersGate
	const shouldShowHeroBar = shouldShowDashboardChrome || shouldShowEmptyNumbersGate
	const homeLayoutClassName = [
		'scout-home-layout',
		firstConnectionAccount ? 'scout-home-layout--first-connection' : '',
		shouldShowEmptyNumbersGate ? 'scout-home-layout--empty-numbers' : '',
	].filter(Boolean).join(' ')
	const shouldShowSidebarCard = Boolean(
		shouldShowDashboardChrome && !firstConnectionAccount && sidebarCard?.visible && !sidebarCardDismissed,
	)
	const shouldReserveSidebarCardSpace = Boolean(enterprise?.plan?.self_service && shouldShowSidebarCard)

	const isInvalidDocument = () => {
	  if (!documentValue)
	  	return true

	  if (documentType === "cpf")
	    return !isValidCPF(documentValue)

	  if (documentType === "cnpj")
	    return !isValidCNPJ(documentValue)

	  return true
	}

	return (
		<div className="scout-page">
			{!showGreetingModal && documentDialog?.open && 
				<DefaultModal
					handleClose={() => {
						setDocumentDialog(null)
						setDocumentValue("")
					}}
					title="Informações adicionais"
					maxWidth="xs"
					subtitle="Nos informe abaixo o número do documento para emissão da Nota Fiscal."
					actionAlign="right"
					action={
						<div style={{ display: "flex", gap: 12 }}>
							<RoundedButton rounded="outlined" onClick={() => {
								setDocumentDialog(null)
								setActiveModalName("")
							}} sx={{ flex: 1 }}>
								Cancelar
							</RoundedButton>
							<RoundedButton onClick={async () => {
								await updateEnterpriseDocument(enterprise?.id, documentType, documentValue)
								setEnterprise((prev) => ({
									...prev,
									cnpj: documentValue,
								}))
								const plan = documentDialog?.plan
								setDocumentDialog(null)
								await handleCheckoutPlan(plan, true, annualDiscount)
							}} disabled={isInvalidDocument()} sx={{ flex: 1 }}>
								Salvar
							</RoundedButton>
						</div>
					}
				>
					<div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
						<FormControl fullWidth>
							<RadioGroup
								row
								value={documentType}
								onChange={(e) => {
									setDocumentType(e.target.value)
									setDocumentValue("")
								}}
							>
								<FormControlLabel value="cpf" control={<Radio />} label="CPF" />
								<FormControlLabel value="cnpj" control={<Radio />} label="CNPJ" />
							</RadioGroup>
						</FormControl>
						<TextField
							fullWidth
							size="small"
							label={documentType === "cpf" ? "CPF" : "CNPJ"}
							placeholder={documentType === "cpf" ? CPF_MASK : CNPJ_MASK}
							value={documentValue}
							onChange={(e) => setDocumentValue(e.target.value.toUpperCase())}
							InputProps={{
								inputComponent: IMaskInput,
								inputProps: {
									mask: mask,
									definitions: {
										"0": /[0-9]/,
										"A": /[a-zA-Z0-9]/,
									},
									unmask: false,
								},
							}}
						/>
					</div>
				</DefaultModal>
			}

			{shouldShowDashboardChrome && !showGreetingModal && searchResultsOpen ? (
				<SearchResultsModal
					open={searchResultsOpen}
					searchValue={searchValue}
					searchResultsLoading={searchResultsLoading}
					searchFeedback={searchFeedback}
					searchResults={normalizedSearchResults}
					onClose={handleCloseSearchResults}
					onOpenSearchConversation={handleOpenSearchConversation}
				/>
			) : null}
			
			{shouldShowDashboardChrome && !showGreetingModal && activeModal ? (
				<DefaultModal
					handleClose={handleCloseActiveModal}
					maxWidth={activeModalName === 'trial-plans' || activeModalName === 'upgrade-plans' ? 'md' : 'sm'}
					paperClassName={activeModalName === 'trial-plans' || activeModalName === 'upgrade-plans' ? 'default-modal__paper--wide' : ''}
					title={activeModal?.title || ''}
					subtitle={activeModal?.subtitle || ''}
					icon={activeModal?.icon || null}
					contentTitle={activeModal?.contentTitle || ''}
					contentDescription={activeModal?.contentDescription || ''}
					actionClassName={activeModalName === 'trial-plans' || activeModalName === 'upgrade-plans' ? 'default-modal__actions--end' : ''}
					action={activeModal?.action || null}
				>
					{activeModalName === 'trial-plans' || activeModalName === 'upgrade-plans'
						? modalPlans.length ? (
							<>
								{(isFreePlan(enterprise) || activeModalName !== "upgrade-plans") &&
									<div style={{ display: "flex", alignItems: "center" }}>
										<span>Mensal</span>
										<Switch
											checked={annualDiscount}
											onChange={(event) => setAnnualDiscount(event.target.checked)}
										/>
										<span>Anual (com desconto)</span>
									</div>
								}
								<div className={[
									'scout-payment-modal__body',
									'scout-plan-grid',
									modalPlans.length === 4 ? 'scout-plan-grid--quartet' : '',
								].filter(Boolean).join(' ')}>
									{modalPlans.map((plan) => (
										<article key={plan.slug} className={[
											'scout-plan-card',
											plan.popular ? 'scout-plan-card--popular' : '',
											modalPlans.length === 4 ? 'scout-plan-card--dense' : '',
										].filter(Boolean).join(' ')}>
											{plan.badge ? <span className="scout-plan-card__badge">{plan.badge}</span> : null}
											<h3>
												{plan.icon}
												{plan.name}
												{annualDiscount && plan.discountBadge && (
													<Tooltip title={plan.discountBadge}>
														<DiscountIcon color="info" />
													</Tooltip>
												)}
											</h3>
					                        <Typography variant="subtitle2" className="scout-plan-card__description">
					                            {plan.description}
					                        </Typography>
											<PlanPrice plan={plan} annualDiscount={annualDiscount} />
											<ul className="scout-plan-card__features">
												{(plan.features || []).map((feature) => (
													<li key={`${plan.slug}-${feature}`}>
														<span className="compare-yes">✔</span>
														<span>{feature}</span>
													</li>
												))}
											</ul>
											<button
												type="button"
												className={plan.popular ? 'scout-plan-card__button scout-plan-card__button--primary' : 'scout-plan-card__button'}
												onClick={() => handleCheckoutPlan(plan, false, annualDiscount)}
											>
												{plan.buttonLabel || 'Assinar plano'}
											</button>
										</article>
									))}
								</div>
							</>
						) : (<div className="default-modal__empty-state">Não há planos acima do seu pacote atual.</div>)
						: null
					}
				</DefaultModal>
			) : null}

			{shouldShowHeroBar ? (
				<section className="scout-hero-bar">
					<div>
						<h1 className="scout-hero-bar__title">Olá {greetingName}, {getGreetings()}!</h1>
						<p className="scout-hero-bar__subtitle">{greetingSubtitle}</p>
					</div>

					{shouldShowDashboardChrome ? (
						<div className="scout-filter-bar">
							<form className="scout-search-field" onSubmit={handleSearchSubmit}>
								<SearchIcon />
								<input
									type="search"
									value={searchValue}
									onChange={(event) => setSearchValue(event.target.value)}
									placeholder="Buscar nas conversas"
								/>
							</form>

							<div className="scout-date-dropdown" ref={dateDropdownRef}>
								<button
									type="button"
									className={[
										'scout-date-field',
										'scout-date-field--button',
										isDateMenuOpen ? 'scout-date-field--open' : '',
									].filter(Boolean).join(' ')}
									onClick={() => setIsDateMenuOpen((currentValue) => !currentValue)}
									aria-haspopup="listbox"
									aria-expanded={isDateMenuOpen}
								>
									<span className="scout-date-field__value">{selectedDateLabel}</span>
									<KeyboardArrowDownIcon className={isDateMenuOpen ? 'scout-date-field__chevron scout-date-field__chevron--open' : 'scout-date-field__chevron'} />
								</button>

								{isDateMenuOpen ? (
									<div className="scout-date-dropdown__menu" role="listbox" aria-label="Selecionar período do dashboard">
										{dateOptions.map((option) => {
											const isActive = option.key === selectedDatePreset

											return (
												<button
													key={option.key || option.value}
													type="button"
													className={isActive ? 'scout-date-dropdown__option scout-date-dropdown__option--active' : 'scout-date-dropdown__option'}
													onClick={() => {
														setDateValue(option.endDate || option.value)
														setSelectedDatePreset(option.key || '')
														setIsDateMenuOpen(false)
													}}
													role="option"
													aria-selected={isActive}
												>
													<span>{option.label}</span>
													{isActive ? <span className="scout-date-dropdown__option-indicator" /> : null}
												</button>
											)
										})}
									</div>
								) : null}
							</div>
						</div>
					) : null}
				</section>
			) : null}

			<div className={homeLayoutClassName}>
				{showInitialLoading ? (
					<div className="scout-home-loading" aria-live="polite" aria-busy="true">
						<aside className="scout-side-column">
							<section className="scout-panel scout-panel--sidebar scout-loading-panel">
								<div className="scout-panel__heading">
									<span className="scout-loading-chip" />
									<span className="scout-loading-chip" />
								</div>
								<div className="scout-account-list">
									{Array.from({ length: 3 }).map((_, index) => (
										<div key={`account-loading-${index}`} className="scout-account-card scout-loading-block scout-loading-block--account">
											<div className="scout-account-card__avatar scout-loading-orb" />
											<div className="scout-account-card__content">
												<span className="scout-loading-line scout-loading-line--title" />
												<span className="scout-loading-line scout-loading-line--text" />
											</div>
										</div>
									))}
								</div>
							</section>
						</aside>

						<section className="scout-main-column">
							<div className="scout-stats-grid">
								{Array.from({ length: 4 }).map((_, index) => (
									<article key={`stat-loading-${index}`} className="scout-stat-card scout-loading-block">
										<div className="scout-stat-card__header">
											<span className="scout-loading-line scout-loading-line--text scout-loading-line--medium" />
											<span className="scout-loading-dot" />
										</div>
										<div className="scout-stat-card__value-group">
											<span className="scout-loading-line scout-loading-line--value" />
										</div>
									</article>
								))}
							</div>

							<HomeGraphicsSection loading />
						</section>
					</div>
				) : shouldShowEmptyNumbersGate ? (
					<EmptyScoutNumbersGate
						onCreated={refreshScoutAccounts}
						showSnackbar={showSnackbar}
						user={user}
					/>
				) : firstConnectionAccount ? (
					<FirstConnectionGate
						account={firstConnectionAccount}
						onConnected={refreshScoutAccounts}
						onSendQrByEmail={handleSendConnectionQrEmail}
						showSnackbar={showSnackbar}
					/>
				) : (
					<>
						<aside className="scout-side-column">
							<section
								className={[
									'scout-panel',
									'scout-panel--sidebar',
									'scout-panel--home-sidebar',
									shouldReserveSidebarCardSpace ? 'scout-panel--home-sidebar-with-card' : '',
								].filter(Boolean).join(' ')}
							>
								<div className="scout-panel__heading">
									<h2>Números ativos</h2>
									<button type="button" className="scout-link-button" onClick={() => navigate('/configuracoes/telefones')}>
										{homeConfig?.accountList?.manageLabel || 'Gerenciar números'}
									</button>
								</div>

								{shouldShowAccountFilter ? (
									<div className="scout-account-filter-controls">
										<TextField
											select
											size="small"
											label="Filtro"
											className="scout-account-filter-control"
											value={accountFilterBy}
											InputLabelProps={{ shrink: true }}
											onChange={(event) => setAccountFilterBy(event.target.value)}
										>
											{SCOUT_ACCOUNT_FILTER_OPTIONS.map((option) => (
												<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
											))}
										</TextField>
										<TextField
											size="small"
											label="Buscar"
											className="scout-account-filter-control"
											placeholder={accountFilterBy === 'phone_number' ? 'Buscar por número' : 'Buscar por nome'}
											value={accountFilterText}
											InputLabelProps={{ shrink: true }}
											onChange={(event) => setAccountFilterText(event.target.value)}
										/>
									</div>
								) : null}

								<div
									className={[
										'scout-account-list-scroll',
										isAccountListRefreshing ? 'scout-account-list-scroll--refreshing' : '',
									].filter(Boolean).join(' ')}
									onScroll={handleAccountsListScroll}
								>
									{accountsWithDecorators.length ? (
										<div className="scout-account-list">
											{accountsWithDecorators.map((account) => {
												const active = isScoutAccountActive(account)
												const selected = String(account.id) === String(selectedAccountId)
												const statusIcon = account?.decorator?.statusIcon || (active ? 'arrow' : 'clock')
												const statusTooltip = getAccountStatusTooltip({ active, statusIcon })

												return (
													<div
														key={account.id}
														className={[
															'scout-account-card',
															selected ? 'scout-account-card--selected' : '',
															isTrialExpired ? 'scout-account-card--disabled' : '',
														].filter(Boolean).join(' ')}
													>
														<button
															type="button"
															className="scout-account-card__avatar-action"
															onClick={() => handleAccountClick(account)}
															aria-label={`Filtrar por ${account.name}`}
														>
															<AccountAvatar account={account} decorator={account.decorator} active={active} />
														</button>
														<button
															type="button"
															className="scout-account-card__main-action"
															onClick={() => handleAccountOpen(account)}
														>
															<div className="scout-account-card__content">
																<strong>{account.name}</strong>
																<span>{formatPhoneNumber(account.phone_number)}</span>
															</div>
															{typeof account?.decorator?.unreadBadge === 'number' && account.decorator.unreadBadge > 0 ? (
																<span className="scout-account-card__badge">{account.decorator.unreadBadge}</span>
															) : null}
															{statusTooltip ? (
																<Tooltip title={statusTooltip}>
																	{getAccountStatusIcon(statusIcon)}
																</Tooltip>
															) : getAccountStatusIcon(statusIcon)}
														</button>
													</div>
												)
											})}
											{accountsLoadingMore ? (
												<p className="scout-empty-state">Carregando...</p>
											) : null}
										</div>
									) : shouldShowAccountsEmptyState ? (
										<p className="scout-empty-state">Nenhuma conta encontrada.</p>
									) : null}
								</div>
							</section>
						</aside>

						<section className="scout-main-column">
							<div className={userHasGroup(["administrador"]) ? "scout-stats-grid" : "scout-stats-grid scout-stats-grid--supervisor"}>
								{userHasGroup(["administrador"]) &&
									<article className="scout-stat-card">
										<div className="scout-stat-card__header">
											<span>Licenças</span>
											<SmartphoneIcon />
										</div>
										<div className="scout-stat-card__value-group">
											<strong>{activeAccounts}</strong>
											<small>de {licensesTotal}</small>
										</div>
									</article>
								}

								<article className="scout-stat-card">
									<div className="scout-stat-card__header">
										<span>Grupos</span>
										<GroupsIcon />
									</div>
									<div className="scout-stat-card__value-row">
										<div className="scout-stat-card__value-group">
											<strong>{groupsValue}</strong>
										</div>
										<MetricTrend trend={{
											value: Math.abs(analytics?.groupsChange ?? 0),
											direction: (analytics?.groupsChange ?? 0) >= 0 ? "up" : "down"
										}} />
									</div>
								</article>

								<article className="scout-stat-card">
									<div className="scout-stat-card__header">
										<span>Conversas</span>
										<ForumOutlinedIcon />
									</div>
									<div className="scout-stat-card__value-row">
										<div className="scout-stat-card__value-group">
											<strong>{conversationsValue}</strong>
										</div>
										<MetricTrend trend={{
											value: Math.abs(analytics?.conversationsChange ?? 0),
											direction: (analytics?.conversationsChange ?? 0) >= 0 ? "up" : "down"
										}} />
									</div>
								</article>

								<article className="scout-stat-card">
									<div className="scout-stat-card__header">
										<span>Mensagens</span>
										<MessageIcon />
									</div>
									<div className="scout-stat-card__value-row">
										<div className="scout-stat-card__value-group">
											<strong>{messagesValue}</strong>
										</div>
										<MetricTrend trend={{
											value: Math.abs(analytics?.totalMessagesChange ?? 0),
											direction: (analytics?.totalMessagesChange ?? 0) >= 0 ? "up" : "down"
										}} />
									</div>
								</article>
							</div>

							<HomeGraphicsSection
								analytics={analytics}
								selectedAccount={selectedAccount}
								useMockData={Boolean(homeConfig?.graphics?.useMockData ?? true)}
								enterprise={enterprise}
							/>

							{showRefreshingState ? <p className="scout-inline-feedback">Atualizando dados...</p> : null}
						</section>
					</>
				)}
			</div>

			{enterprise?.plan?.self_service &&
				<FloatingSidebarCard
					card={sidebarCard}
					visible={shouldShowSidebarCard}
					defaultIcon={<SidebarCardIcon />}
					onAction={handleConfiguredAction}
					onDismiss={() => setSidebarCardDismissed(true)}
				/>
			}

			{showGreetingModal &&
				<DefaultModal
				    handleClose={() => setShowGreetingModal(false)}
				    maxWidth="xs"
				    icon={GREETING_CONFIG[showGreetingModal]?.icon}
				>
				    <Box textAlign="center" display="flex" flexDirection="column" gap={1}>
				        <Typography variant="h6">
				            {GREETING_CONFIG[showGreetingModal]?.message}
				        </Typography>
				        <Typography variant="subtitle">
				            {GREETING_CONFIG[showGreetingModal]?.sub}
				        </Typography>
				    </Box>
				</DefaultModal>
			}

			{showConfirmation &&
				<DefaultModal
					handleClose={() => setShowConfirmation(false)}
					title={showConfirmation.title ?? "Confirmação"}
					subtitle={showConfirmation.subtitle}
					maxWidth="xs"
					action={
						<div style={{ display: "flex", gap: 12 }}>
							<RoundedButton rounded="outlined" onClick={() => setShowConfirmation(false)}>
								Não
							</RoundedButton>
							<RoundedButton onClick={showConfirmation.action}>
								Sim
							</RoundedButton>
						</div>
					}
				/>
			}
		</div>
	)
}

const HomeWithBoundary = (props) => (
    <ErrorBoundary
        componentName="Home"
        fullScreen
        showReload
        logToBackend
        title="Erro na página inicial"
        message="Ocorreu um erro inesperado na página inicial. Tente recarregar."
    >
        <Home {...props} />
    </ErrorBoundary>
)

export default HomeWithBoundary
